import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Flex, Tooltip, useToast } from '@chakra-ui/react';
import { get } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Text } from '@revelio/ui';

import { CompanySelectionModal } from '../../components/CompanySelectionModal';
import { resolveSelectionListItemLabel } from '../../components/custom-filters/data-builder-selection-list-mapping';
import { useDataBuilderSelectionLists } from '../../components/custom-filters/useDataBuilderSelectionLists';
import { UnitSelectionModal } from '../../components/UnitSelectionModal';
import type {
  AvailableCustomFilterDefinitions,
  DataBuilderSettingValue,
  DatasetInstanceConfig,
  ModelVersionSelections,
  SelectionListSelectedItem,
} from '../../data-builder.store';
import { useDataBuilderActions } from '../../data-builder.store';
import type {
  DeliverableColumnConfig,
  DeliverableSetting,
  UnitOfAnalysisOption,
} from '../../datasets/deliverable-column-config';
import { collectDefaultColumnKeys } from '../../datasets/initial-instance-config';
import { formatSelectionSummary } from '../../utils/selection-summary';
import {
  STEP2_VALIDATION_SECTION_AGGREGATION,
  STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS,
} from '../step2-columns-validation';
import { AggregationColumnsSection } from './ColumnsSections';
import { hasCompanyUnitOfAnalysisScope } from './company-unit-of-analysis-scope';
import { FileSections } from './FileSection';
import {
  buildConditionContext,
  collectDefaultKeysFromAggregationItems,
  collectRequiredKeysFromAggregationItems,
  filterAggregationItemsByModels,
  filterBreakdownsByModels,
  filterColumnOptions,
  filterCustomFilterCategoriesByCondition,
  filterCustomFilterDefinitionsByCondition,
  filterUnitOptionsByModels,
  getVisibleColumnKeys,
  isAggregationColumnsVisible,
  mergeVisibleSelectedWithRequiredKeys,
} from './filter-config-by-models';
import { GeneralFiltersSection } from './GeneralFiltersSection';
import {
  getDisabledAggregationKeys,
  getDisabledCategoryLabels,
} from './get-disabled-aggregation-keys';
import { SettingsSection } from './SettingsSection';
import { UnitOfAnalysisSection } from './UnitOfAnalysisSection';

const AGGREGATION_BREAKDOWN_ID = 'aggregation';

function joinEnglishList(items: string[]): string {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0] ?? '';
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function columnKeySetsDiffer(next: string[], prev: string[]): boolean {
  if (next.length !== prev.length) {
    return true;
  }
  const prevSet = new Set(prev);
  return next.some((key) => !prevSet.has(key));
}

function withRequiredCompanyColumn(
  keys: string[],
  breakdownId: string,
  options: { key: string; isRequired: boolean }[],
  disabledKeys: Set<string>
): string[] {
  const needsCompany = options.some(
    (option) => option.key === 'company' && option.isRequired
  );
  if (!needsCompany || keys.includes('company')) {
    return keys;
  }
  if (breakdownId === AGGREGATION_BREAKDOWN_ID && disabledKeys.has('company')) {
    return keys;
  }
  return [...keys, 'company'];
}

function isValidSettingValue(
  setting: DeliverableSetting,
  value: DataBuilderSettingValue | undefined
): boolean {
  if (setting.type === 'toggle') {
    return typeof value === 'boolean';
  }

  return (
    typeof value === 'string' &&
    setting.items.some((item: { value: string }) => item.value === value)
  );
}

type Step2ColumnsContentProps = {
  config: DeliverableColumnConfig;
  activeConfig: DatasetInstanceConfig;
  activeInstanceId: string;
  modelVersions: ModelVersionSelections;
  onSetSetting: (key: string, value: DataBuilderSettingValue) => void;
  onSetUnitOfAnalysis: (value: string | null) => void;
  onSetColumnKeysForBreakdown: (
    instanceId: string,
    breakdownId: string,
    keys: string[]
  ) => void;
  onSetColumnKey: (breakdownId: string, key: string, selected: boolean) => void;
  onApplyCompanySelections: (
    selections: DatasetInstanceConfig['companySelections']
  ) => void;
  onApplyCompanyMapper: (
    mapper: DatasetInstanceConfig['companyMapper']
  ) => void;
  onApplyUnitOfAnalysisSelections: (items: SelectionListSelectedItem[]) => void;
  showValidationErrors: boolean;
  sectionErrors: Record<string, string>;
};

export const Step2ColumnsContent = ({
  config,
  activeConfig,
  activeInstanceId,
  modelVersions,
  onSetSetting,
  onSetUnitOfAnalysis,
  onSetColumnKeysForBreakdown,
  onSetColumnKey,
  onApplyCompanySelections,
  onApplyCompanyMapper,
  onApplyUnitOfAnalysisSelections,
  showValidationErrors,
  sectionErrors,
}: Step2ColumnsContentProps) => {
  const toast = useToast();
  const {
    settings,
    unitOfAnalysis,
    aggregationColumns,
    breakdowns,
    generalFilters,
  } = config;
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [unitModalOption, setUnitModalOption] =
    useState<UnitOfAnalysisOption | null>(null);
  const previousAvailableFilterDefinitionsRef = useRef<string | null>(null);
  const previousVisibleKeysRef = useRef<Map<string, Set<string>>>(new Map());
  const {
    pruneInvalidCustomFilters,
    setCompanySets,
    excludeBreakdown,
    includeBreakdown,
  } = useDataBuilderActions();
  const conditionContext = useMemo(
    () =>
      buildConditionContext(
        modelVersions,
        activeConfig.unitOfAnalysis,
        activeConfig.settings
      ),
    [modelVersions, activeConfig.unitOfAnalysis, activeConfig.settings]
  );
  const visibleUnitOptions = filterUnitOptionsByModels(
    unitOfAnalysis.options,
    conditionContext
  );
  const aggregationColumnsVisible = isAggregationColumnsVisible(
    aggregationColumns,
    conditionContext
  );
  const visibleAggregationOptions = useMemo(
    () =>
      aggregationColumnsVisible
        ? filterColumnOptions(aggregationColumns.options, conditionContext)
        : [],
    [aggregationColumnsVisible, aggregationColumns.options, conditionContext]
  );
  const visibleAggregationItems = useMemo(
    () =>
      aggregationColumnsVisible
        ? filterAggregationItemsByModels(
            aggregationColumns.items,
            conditionContext
          )
        : [],
    [aggregationColumnsVisible, aggregationColumns.items, conditionContext]
  );
  const modelVisibleBreakdowns = useMemo(
    () => filterBreakdownsByModels(breakdowns, conditionContext),
    [breakdowns, conditionContext]
  );
  const includedBreakdownIdsSet = useMemo(
    () => new Set(activeConfig.includedBreakdownIds),
    [activeConfig.includedBreakdownIds]
  );
  const visibleBreakdowns = useMemo(
    () =>
      modelVisibleBreakdowns.filter((b) => includedBreakdownIdsSet.has(b.id)),
    [modelVisibleBreakdowns, includedBreakdownIdsSet]
  );
  const addableBreakdowns = useMemo(
    () =>
      modelVisibleBreakdowns.filter((b) => !includedBreakdownIdsSet.has(b.id)),
    [modelVisibleBreakdowns, includedBreakdownIdsSet]
  );
  const visibleGeneralCustomFilterDefinitions = useMemo(
    () =>
      filterCustomFilterDefinitionsByCondition(
        generalFilters.customFilterDefinitions,
        conditionContext
      ),
    [generalFilters.customFilterDefinitions, conditionContext]
  );
  const visibleGeneralCustomFilterCategories = useMemo(
    () =>
      filterCustomFilterCategoriesByCondition(
        generalFilters.customFilterCategories,
        conditionContext
      ),
    [generalFilters.customFilterCategories, conditionContext]
  );
  const visibleAggregationCustomColumnFilterDefinitions = useMemo(
    () =>
      filterCustomFilterDefinitionsByCondition(
        aggregationColumns.customColumnFilterDefinitions,
        conditionContext
      ),
    [aggregationColumns.customColumnFilterDefinitions, conditionContext]
  );
  const visibleAggregationCustomColumnFilterCategories = useMemo(
    () =>
      filterCustomFilterCategoriesByCondition(
        aggregationColumns.customColumnFilterCategories,
        conditionContext
      ),
    [aggregationColumns.customColumnFilterCategories, conditionContext]
  );
  const visibleBreakdownsWithFilteredCustomFilters = useMemo(
    () =>
      visibleBreakdowns.map((b) => ({
        ...b,
        customFilterDefinitions: filterCustomFilterDefinitionsByCondition(
          b.customFilterDefinitions,
          conditionContext
        ),
        customFilterCategories: filterCustomFilterCategoriesByCondition(
          b.customFilterCategories,
          conditionContext
        ),
        customColumnFilterDefinitions: filterCustomFilterDefinitionsByCondition(
          b.customColumnFilterDefinitions,
          conditionContext
        ),
        customColumnFilterCategories: filterCustomFilterCategoriesByCondition(
          b.customColumnFilterCategories,
          conditionContext
        ),
      })),
    [visibleBreakdowns, conditionContext]
  );
  const hasUnitOfAnalysis = get(visibleUnitOptions, 'length', 0) > 0;

  const disabledAggregationKeys = useMemo(
    () =>
      getDisabledAggregationKeys(
        activeConfig.unitOfAnalysis,
        visibleAggregationItems
      ),
    [activeConfig.unitOfAnalysis, visibleAggregationItems]
  );

  const hasCompanyScope = hasCompanyUnitOfAnalysisScope(activeConfig);

  useEffect(() => {
    const availableCustomFilterDefinitions: AvailableCustomFilterDefinitions = {
      general: visibleGeneralCustomFilterDefinitions,
      byBreakdown: Object.fromEntries(
        visibleBreakdownsWithFilteredCustomFilters.map((breakdown) => [
          breakdown.id,
          breakdown.customFilterDefinitions,
        ])
      ),
    };
    const currentAvailableFilterDefinitions = JSON.stringify(
      availableCustomFilterDefinitions
    );

    if (
      previousAvailableFilterDefinitionsRef.current ===
      currentAvailableFilterDefinitions
    ) {
      return;
    }

    previousAvailableFilterDefinitionsRef.current =
      currentAvailableFilterDefinitions;

    pruneInvalidCustomFilters(
      activeInstanceId,
      availableCustomFilterDefinitions
    );
  }, [
    activeInstanceId,
    pruneInvalidCustomFilters,
    visibleBreakdownsWithFilteredCustomFilters,
    visibleGeneralCustomFilterDefinitions,
  ]);

  useEffect(() => {
    for (const setting of settings) {
      const currentValue = activeConfig.settings[setting.key];
      if (isValidSettingValue(setting, currentValue)) {
        continue;
      }

      onSetSetting(setting.key, setting.default);
    }
  }, [activeConfig.settings, onSetSetting, settings]);

  useEffect(() => {
    const prevKeysMap = previousVisibleKeysRef.current;

    const getNewlyVisibleDefaultKeys = (
      breakdownId: string,
      visibleKeys: Set<string>,
      defaultKeys: string[]
    ): string[] => {
      const prevVisible = prevKeysMap.get(breakdownId);
      if (prevVisible === undefined) {
        return [];
      }
      return defaultKeys.filter(
        (k) => visibleKeys.has(k) && !prevVisible.has(k)
      );
    };

    const visibleAggregationKeys = getVisibleColumnKeys(
      visibleAggregationItems
    );
    const requiredAggregationKeys = collectRequiredKeysFromAggregationItems(
      visibleAggregationItems
    );
    const defaultAggregationKeys = collectDefaultKeysFromAggregationItems(
      visibleAggregationItems
    );
    const selectedAggregationKeys =
      activeConfig.columnKeysByBreakdown[AGGREGATION_BREAKDOWN_ID];

    if (selectedAggregationKeys !== undefined) {
      const newlyVisibleDefaults = getNewlyVisibleDefaultKeys(
        AGGREGATION_BREAKDOWN_ID,
        visibleAggregationKeys,
        defaultAggregationKeys
      );
      const mergedAggregationKeys = mergeVisibleSelectedWithRequiredKeys(
        selectedAggregationKeys,
        visibleAggregationKeys,
        requiredAggregationKeys,
        newlyVisibleDefaults
      );
      const filteredAggregationKeys = mergedAggregationKeys.filter(
        (key) => !disabledAggregationKeys.has(key)
      );
      const nextAggregationKeys = hasCompanyScope
        ? withRequiredCompanyColumn(
            filteredAggregationKeys,
            AGGREGATION_BREAKDOWN_ID,
            visibleAggregationOptions,
            disabledAggregationKeys
          )
        : filteredAggregationKeys;
      const clearedByUnitOfAnalysisDisable = mergedAggregationKeys.filter(
        (key) => disabledAggregationKeys.has(key)
      );

      if (columnKeySetsDiffer(nextAggregationKeys, selectedAggregationKeys)) {
        onSetColumnKeysForBreakdown(
          activeInstanceId,
          AGGREGATION_BREAKDOWN_ID,
          nextAggregationKeys
        );
        if (clearedByUnitOfAnalysisDisable.length > 0) {
          const categoryLabels = getDisabledCategoryLabels(
            activeConfig.unitOfAnalysis
          );
          if (categoryLabels.length > 0) {
            toast({
              title: `${joinEnglishList(
                categoryLabels
              )} aggregation columns were cleared because the selected unit of analysis changed.`,
              status: 'info',
              duration: 5000,
              isClosable: true,
              position: 'top-right',
            });
          }
        }
      }
    }
    prevKeysMap.set(AGGREGATION_BREAKDOWN_ID, visibleAggregationKeys);

    for (const breakdown of breakdowns) {
      const selectedBreakdownKeys =
        activeConfig.columnKeysByBreakdown[breakdown.id];

      const visibleBreakdown = visibleBreakdowns.find(
        (item) => item.id === breakdown.id
      );
      const visibleBreakdownKeys =
        visibleBreakdown === undefined
          ? new Set<string>()
          : getVisibleColumnKeys(visibleBreakdown.columnItems);

      if (selectedBreakdownKeys === undefined) {
        prevKeysMap.set(breakdown.id, visibleBreakdownKeys);
        continue;
      }

      const requiredBreakdownKeys =
        visibleBreakdown === undefined
          ? []
          : collectRequiredKeysFromAggregationItems(
              visibleBreakdown.columnItems
            );
      const defaultBreakdownKeys =
        visibleBreakdown === undefined
          ? []
          : collectDefaultKeysFromAggregationItems(
              visibleBreakdown.columnItems
            );
      const newlyVisibleDefaults = getNewlyVisibleDefaultKeys(
        breakdown.id,
        visibleBreakdownKeys,
        defaultBreakdownKeys
      );
      const mergedBreakdownKeys = mergeVisibleSelectedWithRequiredKeys(
        selectedBreakdownKeys,
        visibleBreakdownKeys,
        requiredBreakdownKeys,
        newlyVisibleDefaults
      );
      const nextBreakdownKeys =
        hasCompanyScope && visibleBreakdown !== undefined
          ? withRequiredCompanyColumn(
              mergedBreakdownKeys,
              breakdown.id,
              visibleBreakdown.columnOptions,
              disabledAggregationKeys
            )
          : mergedBreakdownKeys;

      if (columnKeySetsDiffer(nextBreakdownKeys, selectedBreakdownKeys)) {
        onSetColumnKeysForBreakdown(
          activeInstanceId,
          breakdown.id,
          nextBreakdownKeys
        );
      }
      prevKeysMap.set(breakdown.id, visibleBreakdownKeys);
    }
  }, [
    activeConfig.columnKeysByBreakdown,
    activeConfig.unitOfAnalysis,
    activeInstanceId,
    breakdowns,
    disabledAggregationKeys,
    hasCompanyScope,
    onSetColumnKeysForBreakdown,
    toast,
    visibleAggregationItems,
    visibleAggregationOptions,
    visibleBreakdowns,
  ]);

  useEffect(() => {
    if (activeConfig.unitOfAnalysis === null) {
      return;
    }

    const hasSelectedVisibleOption = visibleUnitOptions.some(
      (option) => option.value === activeConfig.unitOfAnalysis
    );

    if (!hasSelectedVisibleOption) {
      onSetUnitOfAnalysis(null);
    }
  }, [activeConfig.unitOfAnalysis, onSetUnitOfAnalysis, visibleUnitOptions]);

  // Resolve ID-only labels in unit of analysis selections using selection lists.
  // When hydrating from a payload the labels are just numeric IDs.
  const uoaSelectionListKeys = useMemo(() => {
    const selectedOption = visibleUnitOptions.find(
      (o) => o.value === activeConfig.unitOfAnalysis
    );
    return selectedOption?.selectionListKeys ?? [];
  }, [visibleUnitOptions, activeConfig.unitOfAnalysis]);

  const { selectionLists: uoaSelectionLists } = useDataBuilderSelectionLists({
    apiKeys: uoaSelectionListKeys,
  });

  useEffect(() => {
    if (
      uoaSelectionLists.length === 0 ||
      activeConfig.unitOfAnalysisSelections.length === 0 ||
      activeInstanceId === null
    ) {
      return;
    }

    const hasUnresolved = activeConfig.unitOfAnalysisSelections.some(
      (item) => item.label === item.id
    );
    if (!hasUnresolved) return;

    let changed = false;
    const resolved = activeConfig.unitOfAnalysisSelections.map((item) => {
      if (item.label !== item.id) return item;
      const label = resolveSelectionListItemLabel(
        item.id,
        item.selectionListId,
        uoaSelectionLists
      );
      if (label) {
        changed = true;
        return { ...item, label };
      }
      return item;
    });

    if (changed) {
      onApplyUnitOfAnalysisSelections(resolved);
    }
  }, [
    uoaSelectionLists,
    activeConfig.unitOfAnalysisSelections,
    activeInstanceId,
    onApplyUnitOfAnalysisSelections,
  ]);

  const handleUnitOptionClick = (value: string) => {
    if (value === 'company') {
      setIsCompanyModalOpen(true);
      return;
    }

    const option = visibleUnitOptions.find((o) => o.value === value);
    if (option !== undefined && option.selectionListKeys.length > 0) {
      setUnitModalOption(option);
      return;
    }

    onSetUnitOfAnalysis(value);
  };

  const companySelectionSummary = (() => {
    if (activeConfig.companyMapper !== null) {
      const selectionCount = Object.keys(activeConfig.companySelections).length;
      if (selectionCount > 0) {
        return formatSelectionSummary({
          type: 'company_mapper_with_selections',
          count: selectionCount,
        });
      }
      return formatSelectionSummary({ type: 'company_mapper' });
    }

    if (
      activeConfig.companySets !== null &&
      activeConfig.companySets.length > 0
    ) {
      return formatSelectionSummary({
        type: 'common_set',
        companySets: activeConfig.companySets,
      });
    }

    return formatSelectionSummary({
      type: 'company_selections',
      count: Object.keys(activeConfig.companySelections).length,
    });
  })();

  const selectionDetails = visibleUnitOptions.reduce<Record<string, string>>(
    (result, option) => {
      if (option.value === 'company') {
        return {
          ...result,
          [option.value]: companySelectionSummary,
        };
      }

      if (activeConfig.unitOfAnalysis !== option.value) {
        return {
          ...result,
          [option.value]: '',
        };
      }

      return {
        ...result,
        [option.value]: formatSelectionSummary({
          type: 'unit_of_analysis',
          selections: activeConfig.unitOfAnalysisSelections,
        }),
      };
    },
    {}
  );

  return (
    <>
      {hasUnitOfAnalysis && (
        <UnitOfAnalysisSection
          unitOptions={visibleUnitOptions}
          selectedUnitOfAnalysis={activeConfig.unitOfAnalysis}
          selectionDetails={selectionDetails}
          onUnitOptionClick={handleUnitOptionClick}
          showValidationErrors={showValidationErrors}
          sectionErrorMessage={
            sectionErrors[STEP2_VALIDATION_SECTION_UNIT_OF_ANALYSIS]
          }
        />
      )}

      <SettingsSection
        settings={settings}
        values={activeConfig.settings}
        onSetSetting={onSetSetting}
      />

      {generalFilters.supportsFilters && (
        <section>
          <Flex alignItems="center" gap={1} mb={2}>
            <Text fontSize="sm" weight="semibold">
              Filters
            </Text>
            <Tooltip
              label='If more than one filter is defined, the output will include union of filter conditions.("OR" is used.) If you want the output to satisfy all the filter conditions, you can do so in a single filter using "AND".'
              textAlign="center"
              hasArrow
              placement="top"
            >
              <InfoOutlineIcon
                boxSize={3}
                color="text.primary"
                cursor="pointer"
              />
            </Tooltip>
          </Flex>
          <GeneralFiltersSection
            generalFilters={{
              ...generalFilters,
              customFilterDefinitions: visibleGeneralCustomFilterDefinitions,
              customFilterCategories: visibleGeneralCustomFilterCategories,
            }}
            activeConfig={activeConfig}
            activeInstanceId={activeInstanceId}
          />
        </section>
      )}

      {visibleAggregationItems.length > 0 && (
        <AggregationColumnsSection
          aggregationColumns={{
            ...aggregationColumns,
            options: visibleAggregationOptions,
            items: visibleAggregationItems,
            customColumnFilterDefinitions:
              visibleAggregationCustomColumnFilterDefinitions,
            customColumnFilterCategories:
              visibleAggregationCustomColumnFilterCategories,
          }}
          activeConfig={activeConfig}
          activeInstanceId={activeInstanceId}
          disabledColumnKeys={disabledAggregationKeys}
          selectedColumnKeys={
            activeConfig.columnKeysByBreakdown[AGGREGATION_BREAKDOWN_ID] ?? []
          }
          onSetColumnKey={(key, selected) =>
            onSetColumnKey(AGGREGATION_BREAKDOWN_ID, key, selected)
          }
          showValidationErrors={showValidationErrors}
          sectionErrorMessage={
            sectionErrors[STEP2_VALIDATION_SECTION_AGGREGATION]
          }
        />
      )}

      {activeConfig.unitOfAnalysis === null ? (
        <Text
          data-testid="data-builder-file-estimate-placeholder"
          fontSize="sm"
          color="text.secondary"
        >
          Select Unit of Analysis
        </Text>
      ) : null}

      <FileSections
        files={visibleBreakdownsWithFilteredCustomFilters}
        addableFiles={addableBreakdowns}
        activeConfig={activeConfig}
        activeInstanceId={activeInstanceId}
        showValidationErrors={showValidationErrors}
        sectionErrors={sectionErrors}
        onSetColumnKey={onSetColumnKey}
        onRemoveFile={(fileId) => excludeBreakdown(activeInstanceId, fileId)}
        onAddFile={(fileId) => {
          const breakdown = breakdowns.find((b) => b.id === fileId);
          const defaultKeys =
            breakdown !== undefined
              ? collectDefaultColumnKeys(breakdown.columnOptions)
              : [];
          includeBreakdown(activeInstanceId, fileId, defaultKeys);
        }}
      />

      <CompanySelectionModal
        instanceId={activeInstanceId}
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        selectedCompanySelections={activeConfig.companySelections}
        selectedCompanySets={activeConfig.companySets}
        selectedCompanyMapper={activeConfig.companyMapper}
        onApplySelections={(selections) => {
          onApplyCompanySelections(selections);
          onSetUnitOfAnalysis(
            Object.keys(selections).length > 0 ? 'company' : null
          );
        }}
        onApplyCompanySets={(companySets) => {
          setCompanySets(activeInstanceId, companySets);
          onSetUnitOfAnalysis('company');
        }}
        onApplyMapper={(mapper) => {
          onApplyCompanyMapper(mapper);
          onSetUnitOfAnalysis('company');
        }}
      />

      {unitModalOption !== null && (
        <UnitSelectionModal
          isOpen
          onClose={() => setUnitModalOption(null)}
          unitLabel={unitModalOption.label}
          selectionListKeys={unitModalOption.selectionListKeys}
          selectedItems={activeConfig.unitOfAnalysisSelections}
          onApply={(items) => {
            if (items.length > 0) {
              onSetUnitOfAnalysis(unitModalOption.value);
              onApplyUnitOfAnalysisSelections(items);
            }
          }}
        />
      )}
    </>
  );
};
